// The agentic-buying control plane, declared centrally with provenance so the
// Governance panel shows the REAL enforced configuration and cites where each
// control is enforced in code — not hardcoded literals presented as live.
//
// Values here mirror the enforced backend configuration:
//   - Network whitelist / per-line / per-plan ceilings: the media-planning
//     agent's guardrail policy (app/agent/orchestration/policy.py, built from
//     env in app/agent/tools/buying_tools.py).
//   - Send kill-switch + $1 media cap: the bpm-lambdas send guard
//     (streaming/streaming_buying.ts).
// The order lifecycle shown in the panel is live, read from real order state.

// Durable guardrails GOVERN the agent in production — approved-inventory and
// spend-discipline limits that stay in force after launch. Rollout-safety
// controls are temporary training wheels (kill-switch, $1 cap) that exist only to
// keep the tool from going too far before it's validated, and come off at go-live.
// The demo should foreground the durable ones — those are the governance an
// analyst grades; the rollout controls are honest context, not the headline.
export const GuardrailKind = {
  DURABLE: 'durable',
  ROLLOUT: 'rollout'
} as const;
export type GuardrailKindType = (typeof GuardrailKind)[keyof typeof GuardrailKind];

export interface GuardrailControl {
  label: string;
  value: string;
  prevents: string;
  envVar: string;
  enforcedAt: string;
  kind: GuardrailKindType;
}

export const AGENT_IDENTITY = {
  identity: 'automation@tinuiti.com',
  scope: 'Least privilege — can draft and hold insertion orders only. It has no send capability.',
  owner: 'Harry Browne, Tinuiti (Media Planning Agent owner)'
} as const;

export const GUARDRAILS: GuardrailControl[] = [
  {
    label: 'Approved-inventory allow-list',
    // Fallback shown when the live governance.policy read is unavailable. The live
    // bind (governance-live.ts) replaces this with the resolved direct-IO whitelist
    // (direct.network_whitelist). Count, not a single network — the approved set is
    // 13 direct-IO networks: OTTA, Tubi, Roku, Amazon, Pluto TV, EyeQ, Peacock AX,
    // IMG College, DirecTV, Disney+, DENT, TEL, Prime.
    value: '13 networks',
    prevents:
      'The agent can only buy on explicitly approved networks — the resolved direct-IO allow-list of 13 networks (OTTA, Tubi, Roku, Amazon, Pluto TV, EyeQ, Peacock AX, IMG College, DirecTV, Disney+, DENT, TEL, Prime). Everything else is rejected before an order is created — approved inventory stays a hard constraint in production.',
    envVar: 'AGENT_IO_NETWORK_WHITELIST',
    enforcedAt: 'media-planning agent — policy.py / buying_tools.py',
    kind: GuardrailKind.DURABLE
  },
  {
    label: 'Per-line spend ceiling',
    value: '$50,000',
    prevents:
      'No single line can exceed the ceiling; over-budget lines fail the guardrail check. A standing spend-discipline limit, not a rollout cap.',
    envVar: 'AGENT_IO_MAX',
    enforcedAt: 'media-planning agent — policy.py / buying_tools.py',
    kind: GuardrailKind.DURABLE
  },
  {
    label: 'Per-plan spend ceiling',
    value: '$250,000',
    prevents:
      'Total agentic spend across a plan is capped — the agent cannot commit more than the mandate allows.',
    envVar: 'AGENT_PLAN_MAX',
    enforcedAt: 'media-planning agent — policy.py / buying_tools.py',
    kind: GuardrailKind.DURABLE
  },
  {
    label: 'Send kill-switch',
    // Fallback label shown when the live read is unavailable. Describes the STATE
    // (fail-closed default = sends blocked), not the switch position — see
    // applyLiveGuards in governance-live.ts.
    value: 'Agentic send → BLOCKED (fail-closed)',
    prevents:
      'Rollout safety: the send path is disabled across the environment (any send is refused 403) until the flow is fully validated. Comes off at go-live.',
    envVar: 'AGENTIC_BUYING_SEND_ENABLED',
    enforcedAt: 'bpm-lambdas — streaming/streaming_buying.ts',
    kind: GuardrailKind.ROLLOUT
  },
  {
    label: 'Global go-live lock',
    // Fallback label shown when the live read is unavailable (fail-closed default
    // = go-live locked). Reports the state, not the switch position.
    value: 'Go-live → LOCKED (fail-closed)',
    prevents:
      'Rollout safety: a second, independent fail-closed lock. Even with the send kill-switch flipped on, no agent order can reach a network (and no future activate path may run) until the go-live lock is ALSO explicitly enabled — two switches must both flip. Comes off at go-live.',
    envVar: 'AGENTIC_BUYING_GO_LIVE_ENABLED',
    enforcedAt: 'bpm-lambdas — streaming/streaming_buying.ts',
    kind: GuardrailKind.ROLLOUT
  },
  {
    label: 'Send media cap',
    value: '$1',
    prevents:
      'Rollout safety: even if every other control failed, an agent order with more than $1 of booked media cannot be sent. A pre-launch failsafe, lifted at go-live.',
    envVar: 'AGENT_MAX_SEND_MEDIA_USD',
    enforcedAt: 'bpm-lambdas — streaming/streaming_buying.ts',
    kind: GuardrailKind.ROLLOUT
  }
];
